export type HorizontalAlign =
  | 'general'
  | 'left'
  | 'center'
  | 'right'
  | 'fill'
  | 'justify'
  | 'centerContinuous'
  | 'distributed';
export type VerticalAlign = 'top' | 'middle' | 'bottom' | 'justify' | 'distributed';

export type BorderStyle =
  | 'thin'
  | 'medium'
  | 'thick'
  | 'dashed'
  | 'dotted'
  | 'double'
  | 'hair'
  | 'mediumDashed'
  | 'dashDot'
  | 'mediumDashDot'
  | 'dashDotDot'
  | 'mediumDashDotDot'
  | 'slantDashDot';

export interface BorderEdge {
  style: BorderStyle;
  color?: string;
}

export interface CellBorders {
  top?: BorderEdge;
  right?: BorderEdge;
  bottom?: BorderEdge;
  left?: BorderEdge;
  diagonalUp?: BorderEdge;
  diagonalDown?: BorderEdge;
}

export interface FontStyle {
  fontName?: string;
  fontSize?: number;
  bold?: boolean;
  italic?: boolean;
  underline?: 'single' | 'double';
  strike?: boolean;
  color?: string;
  vertAlign?: 'superscript' | 'subscript';
}

export interface CellStyle extends FontStyle {
  background?: string;
  align?: HorizontalAlign;
  valign?: VerticalAlign;
  wrap?: boolean;
  indent?: number;
  rotation?: number;
  shrink?: boolean;
  borders?: CellBorders;
}

export interface TextRun {
  text: string;
  font?: FontStyle;
}

export type CellKind = 'empty' | 'number' | 'text' | 'boolean' | 'error';

export interface DataBar {
  color: string;
  ratio: number;
  gradient: boolean;
}

export interface CellIcon {
  symbol: string;
  color: string;
  hideValue: boolean;
}

export interface SheetCell {
  kind: CellKind;
  text: string;
  value?: number | string | boolean;
  formatCode?: string;
  formatColor?: string;
  fill?: { before: string; after: string };
  formula?: string;
  style?: CellStyle;
  runs?: TextRun[];
  dataBar?: DataBar;
  icon?: CellIcon;
  hyperlink?: CellLink;
}

export interface CellLink {
  url?: string;
  location?: string;
  tooltip?: string;
}

export interface SheetMerge {
  rowFrom: number;
  rowTo: number;
  colFrom: number;
  colTo: number;
}

export interface CellAnchor {
  row: number;
  col: number;
  rowOffset: number;
  colOffset: number;
}

export interface SheetImage {
  data: Blob;
  from: CellAnchor;
  to?: CellAnchor;
  width?: number;
  height?: number;
}

export interface Sheet {
  name: string;
  rows: SheetCell[][];
  merges: SheetMerge[];
  columnWidths: (number | undefined)[];
  hiddenColumns: boolean[];
  rowHeights: (number | undefined)[];
  hiddenRows: boolean[];
  defaultColumnWidth?: number;
  baseColumnWidth: number;
  defaultRowHeight?: number;
  showGridLines: boolean;
  frozenRows: number;
  frozenColumns: number;
  zoom: number;
  images: SheetImage[];
  tabColor?: string;
  defaultFont: FontStyle;
  active?: boolean;
  activeCell?: { row: number; col: number };
  date1904?: boolean;
}

export function emptyCell(): SheetCell {
  return { kind: 'empty', text: '' };
}
